import _ from 'lodash'
import { ActionTree } from 'vuex'
import { RootState } from '@/store/types'
import { I18nState } from '@/modules/i18n/store'
import * as api from '@/modules/i18n/api'
import Locale from '@/modules/i18n/model/Locale'
import WorldLocale from '@/modules/i18n/model/WorldLocale'
import Message from '@/modules/i18n/model/Message'
import {MessageListModes, MutationTypes} from '@/modules/i18n/store/mutations'
import { notify } from '@/modules/core/notification';
import Log from '@/modules/log';

const actions: ActionTree<I18nState, RootState> = {
    /**
     * Obtiene la lista de localismos registrados en Artisan y los carga en
     * el estado.
     *
     * Si el parámetro override se activa, en cada ejecución de la acción
     * se invocará a la api.
     */
    loadLocales: async ({commit, getters, state}, payload = { override: false }) => {
        if (state.localeListFullyLoaded && !payload.override) {
            return
        }

        // Recargar lista de localismos
        const locales = state.locales
        const freshLocales = await api.getLocales()

        // Volcar los mensajes obtenidos
        freshLocales.forEach(freshLocale => {
            freshLocale.name = getters.worldLocaleName(freshLocale.identifier)
            const oldLocale = _.find(locales, {identifier: freshLocale.identifier})
            if (_.isUndefined(oldLocale) || oldLocale.messages.length < 1) {
                return
            }

            freshLocale.messages = oldLocale.messages
        })

        commit('setLocales', {locales: freshLocales})
        commit(MutationTypes.MarkLocaleListFullyLoaded, true)
    },

    /**
     * Obtiene un solo localismo del servidor
     *
     * Si ya existe en el estado se obtendrá de este
     */
    loadLocale: async ({getters, commit}, payload: { locale: string }): Promise<Locale> => {
        let locale = getters.locale(payload.locale)
        if (locale) {
            return locale
        }

        locale = await api.getLocale(payload.locale)
        locale.name = getters.worldLocaleName(locale.identifier)
        commit(MutationTypes.SetLocale, { locale })

        return locale
    },

    /**
     * Localismo por defecto
     *
     * Carga el localismo por defecto definido en Artisan, lo registra en el
     * estado y lo retorna
     */
    loadDefaultLocale: async ({state, dispatch, commit}, payload): Promise<Locale> => {
        if (state.defaultLocale) {
            return state.defaultLocale
        }

        const defaultLocale = await dispatch('loadLocale', {locale: 'default'})
        commit(MutationTypes.SetDefaultLocale, {locale: defaultLocale})

        return defaultLocale
    },

    /**
     * Registra un nuevo localismo en Artisan y lo añade a la lista actual
     * en el estado de la aplicación
     */
    createLocale: async ({commit, getters}, payload: { locale: Locale }) => {
        const locale = await api.createLocale(payload.locale)

        // Marca el localismo como "nuevo", para indicar que ha sido creado
        // durante la sesión actual de la aplicación
        locale.isNew = true
        locale.name  = getters.worldLocaleName(locale.identifier)
        commit(MutationTypes.SetLocale, { locale })
    },

    /**
     * Eliminar localismo
     */
    deleteLocale: async ({commit, getters}, payload: {locale: string}) => {
        const result = await api.deleteLocale(payload.locale)
        if (result) {
            commit(MutationTypes.DeleteLocale, {locale: payload.locale})
        }
    },

    /**
     * Carga los mensaje registrados en un localismo
     */
    loadLocaleMessages: async ({dispatch, commit}, payload: { locale: string }) => {
        const locale: Locale = await dispatch('loadLocale', { locale: payload.locale })
        const messages = await api.getLocaleMessages(locale.identifier)

        locale.messages = _.merge(locale.messages, messages)
        commit(MutationTypes.SetLocale, { locale })
    },

    /**
     * Registra un nuevo mensaje en Artisan y lo añade al estado estado
     * actual de la aplicación
     */
    createLocaleMessage: async ({commit, dispatch}, payload: {message: Message}): Promise<Message> => {
        const message = await api.createLocaleMessage(payload.message)

        // Recarga la paginación actual de forma asincrona
        await dispatch('loadMessagePagination')

        // commit(MutationTypes.SetLocaleMessage, { message })

        return message
    },

    /**
     * Actualiza un mensaje en Artisan y registra el cambio en el estado
     * de la aplicación
     */
    updateLocaleMessage: async ({commit}, message: Message): Promise<Message> => {
        message = await api.updateLocaleMessage(message)

        // Registrar en el estado
        // commit(MutationTypes.SetLocaleMessage, { message })

        return message
    },

    /**
     * Elimina el mensaje seleccionado actualmente
     *
     * @param context
     * @returns {Promise<void>}
     */
    deleteSelectedMessage: async ({state, commit, dispatch}) => {
        const message = state.messageList.selectedMessage
        if (!message) {
            return
        }

        try {
            await api.deleteMessage([message])
        } catch (error) {
            Log.error(`No se ha podido eliminar la traducción: ${error.message}`)

            return
        }

        // // Elimina también el mensaje del estado
        // commit(MutationTypes.DeleteLocaleMessage, {
        //     locale: message.locale.toString(),
        //     message: message.key,
        // })

        commit(MutationTypes.SetMessageListSelectedMessage, { message: null })
        notify(`La traducción ${message.key} se ha eliminado correctamente`)

        dispatch('loadMessagePagination')
    },

    /**
     * Carga en el estado la lista con localismos del mundo
     */
    loadWorldLocales: async ({state, commit}) => {
        // La lista de idiomas ya se encuentra registrada en la aplicación
        if (state.worldLocales.length) {
            return
        }

        // Obtener de cache y cargar si no esta registrada
        let cache = JSON.parse(localStorage.getItem('cache.locale_names') || '{}')
        if (_.isEmpty(cache)) {
            cache = await api.getWorldLocales('es_ES')
            localStorage.setItem('cache.locale_names', JSON.stringify(cache))
        }

        const worldLocales: WorldLocale[] = []
        _.forIn(cache, (value: string, key: string) => {
            worldLocales.push(new WorldLocale(key, value))
        })

        commit('setWorldLocales', {worldLocales})
    },

    /**
     * Carga la paginación del servidor
     */
    loadMessagePagination: async ({state, commit}) => {

        // Solicita a la API la paginación
        const pagination = await api.getMessagePagination(
            state.messageList.currentPage,
            state.messageList.recordsPerPage,
            state.messageList.paginationFilters,
        )

        commit(MutationTypes.SetMessagePagination, pagination)
    },

    /**
     * Construye un nuevo mensaje y lo inserta en el estado de la aplicación
     * como mensaje actualmente en edición
     */
    buildNewMessage: ({state, commit, getters}) => {
        if (!getters.currentLocale) {
            return;
        }

        commit(MutationTypes.SetLocaleMessageMode, {mode: MessageListModes.Creating});
        commit(MutationTypes.SetMessageListSelectedMessage, {
            message: new Message('', '', state.messageList.selectedLocale),
        })
    },
}

export default actions
